export const NAV_LINKS = [
  { label: 'Services', path: '/services' },
  { label: 'Pricing', path: '/pricing' },
  { label: 'Business Structure', path: '/business-structure' },
  { label: 'About', path: '/about' },
  { label: 'FAQ', path: '/faq' },
  { label: 'Contact', path: '/contact' },
];

export const SERVICES = [
  {
    id: 'personal-tax',
    title: 'Personal Tax Preparation',
    shortTitle: 'Personal Tax Prep',
    description: 'Comprehensive individual tax preparation for W-2 earners, self-employed professionals, and rental income owners.',
    whoItsFor: 'Individuals with W-2 income, freelancers, gig workers, and those with rental properties.',
    whatsIncluded: [
      'Federal and state return preparation',
      'Deduction and credit optimization',
      'E-filing with IRS and state agencies',
      'Review of prior-year returns for missed opportunities',
      'Post-filing support for any IRS correspondence',
    ],
    docsNeeded: ['W-2s from all employers', 'Valid government-issued ID', '1099 forms (if applicable)', 'Prior-year tax return', 'Mortgage interest statement (1098)', 'Property tax records'],
    scenarios: ['W-2 employees wanting maximum refund', 'Self-employed with Schedule C income', 'Rental property owners (Schedule E)', 'Those with investment income'],
    icon: 'FileText',
    price: 'Starting at $295',
  },
  {
    id: 'business-tax',
    title: 'Business Tax Preparation',
    shortTitle: 'Business Tax Prep',
    description: 'Professional business return preparation for partnerships, S-Corps, and C-Corps.',
    whoItsFor: 'Business owners filing 1065, 1120S, or 1120 returns.',
    whatsIncluded: [
      'Complete business return preparation',
      'K-1 preparation for partners/shareholders',
      'Quarterly estimated tax guidance',
      'Business deduction analysis',
      'State business return filing',
    ],
    docsNeeded: ['Prior-year business return', 'Profit & loss statement', 'Balance sheet', 'Business bank statements', 'Payroll reports', 'Business receipts and expenses'],
    scenarios: ['Partnerships (Form 1065)', 'S-Corporations (Form 1120S)', 'C-Corporations (Form 1120)', 'Multi-member LLCs'],
    icon: 'Building2',
    price: 'Starting at $295',
  },
  {
    id: 'multi-state',
    title: 'Multi-State Returns',
    shortTitle: 'Multi-State',
    description: 'Accurate multi-state filing for those who earned income in more than one state.',
    whoItsFor: 'Individuals or businesses with income, property, or operations in multiple states.',
    whatsIncluded: [
      'State-specific return preparation for each state',
      'Credit allocation between states',
      'Residency and domicile analysis',
      'Remote worker state filing guidance',
      'State-specific deduction optimization',
    ],
    docsNeeded: ['W-2s showing multiple state income', 'State-specific 1099s', 'Documentation of days worked in each state', 'Rental income by state', 'Prior-year state returns'],
    scenarios: ['Remote workers in different states', 'Relocated mid-year', 'Rental properties in multiple states', 'Business income from multiple states'],
    icon: 'Map',
    price: 'Starting at $295',
  },
  {
    id: 'amendments',
    title: 'Amended Returns',
    shortTitle: 'Amendments',
    description: 'Correction of previously filed returns to claim missed deductions, fix errors, or update filing status.',
    whoItsFor: 'Anyone who needs to correct a previously filed federal or state return.',
    whatsIncluded: [
      'Review of original return for errors',
      'Form 1040-X preparation',
      'State amended return (if applicable)',
      'Explanation of changes letter',
      'IRS submission and tracking',
    ],
    docsNeeded: ['Original filed return', 'New or corrected documents', 'Explanation of what changed', 'Supporting documentation for changes'],
    scenarios: ['Missed deductions or credits', 'Incorrect filing status', 'Unreported income correction', 'Claiming education credits retroactively'],
    icon: 'RefreshCw',
    price: '$500',
  },
  {
    id: 'business-structure',
    title: 'Business Structure & Formation',
    shortTitle: 'Business Structure',
    description: 'Tax-focused education on choosing and forming the right business entity for your goals.',
    whoItsFor: 'Entrepreneurs, new business owners, and existing businesses considering restructuring.',
    whatsIncluded: [
      'Entity comparison education (LLC, S-Corp, C-Corp, Partnership)',
      'Tax impact analysis for each structure',
      'Formation filing assistance',
      'EIN application support',
      'First-year tax planning guidance',
    ],
    docsNeeded: ['Business plan or description', 'Projected income estimates', 'Number of owners/members', 'State of formation preference', 'Current business documents (if restructuring)'],
    scenarios: ['Starting a new business', 'Converting sole proprietorship to LLC', 'Electing S-Corp status', 'Multi-member business formation'],
    icon: 'Landmark',
    price: 'Starts at $599',
  },
];

export const PRICING_TIERS = [
  {
    title: 'Tax Preparation',
    price: '$295',
    prefix: 'Starting at',
    description: 'Personal and business tax returns prepared by experienced professionals.',
    features: [
      'Federal + state returns',
      'W-2, Schedule C, Schedule E',
      'Business returns (1065, 1120S, 1120)',
      'Deduction optimization',
      '2-day turnaround after docs received',
      'Post-filing support',
    ],
    highlight: true,
    cta: 'Book Free Consult',
  },
  {
    title: 'Amended Returns',
    price: '$500',
    prefix: '',
    description: 'Correct errors or claim missed deductions on previously filed returns.',
    features: [
      'Original return review',
      'Form 1040-X preparation',
      'State amendments included',
      'IRS submission & tracking',
      'Changes explanation letter',
      'Post-amendment support',
    ],
    highlight: false,
    cta: 'Book Free Consult',
  },
  {
    title: 'Business Structure',
    price: '$599',
    prefix: 'Starts at',
    description: 'Tax education and formation filing for LLCs, S-Corps, and more.',
    features: [
      'Entity type education & comparison',
      'Tax impact analysis',
      'Formation filing assistance',
      'EIN application support',
      'First-year tax planning',
      'Ongoing structure guidance',
    ],
    highlight: false,
    cta: 'Book Free Consult',
  },
  {
    title: 'Expedited Processing',
    price: '$99',
    prefix: '+',
    description: 'Need your return faster? Add expedited processing to any service.',
    features: [
      'Priority processing queue',
      'Faster turnaround time',
      'Available for all services',
      'Add to any package',
    ],
    highlight: false,
    cta: 'Learn More',
  },
];

export const HOW_IT_WORKS = [
  {
    step: 1,
    title: 'Book a Free Consultation',
    description: 'Schedule a quick call to discuss your tax situation and what you need.',
    icon: 'CalendarCheck',
  },
  {
    step: 2,
    title: 'Submit Your Documents',
    description: 'Upload your tax documents securely through our intake form.',
    icon: 'Upload',
  },
  {
    step: 3,
    title: 'We Prepare & Review',
    description: 'Our team prepares your return within 2 business days of receiving all documents.',
    icon: 'ClipboardCheck',
  },
  {
    step: 4,
    title: 'Review, Pay & File',
    description: 'Review your return, receive your invoice, and we file once payment is received.',
    icon: 'CheckCircle',
  },
];

export const TESTIMONIALS = [
  { name: 'Marcus T.', location: 'Atlanta, GA', text: 'The team at Beginning to End Tax made my multi-state filing so simple. Everything was done virtually, and I had my return back in two days. Highly recommend for anyone filing in more than one state.' },
  { name: 'Priya S.', location: 'Chicago, IL', text: 'As a freelancer, taxes always stressed me out. They walked me through every deduction and made sure I understood what I was filing. Professional and patient.' },
  { name: 'David & Lauren K.', location: 'Houston, TX', text: 'We switched to Beginning to End Tax after a bad experience with a chain service. Night and day difference. They caught credits our previous preparer missed.' },
  { name: 'Angela R.', location: 'Phoenix, AZ', text: 'I needed an amendment from a prior year and was dreading the process. Their team handled everything and kept me updated every step of the way. Worth every dollar.' },
  { name: 'James L.', location: 'Brooklyn, NY', text: 'Setting up my LLC was overwhelming until I consulted with them. They explained the tax implications of each structure and helped me make the right choice for my business.' },
  { name: 'Keisha M.', location: 'Charlotte, NC', text: 'Fast, thorough, and affordable. I was skeptical about virtual tax prep at first, but the experience was seamless. Will definitely be back next year.' },
  { name: 'Robert C.', location: 'Denver, CO', text: 'As a small business owner filing an 1120S, I needed someone who understood business returns. They delivered—on time and with clear explanations of everything.' },
  { name: 'Sofia G.', location: 'Miami, FL', text: 'The free consultation alone was incredibly valuable. They answered all my questions and gave me a clear picture of what to expect. No pressure, just helpful guidance.' },
  { name: 'Terrence W.', location: 'Seattle, WA', text: 'I\'ve been filing my own taxes for years but always worried I was leaving money on the table. Beginning to End Tax found deductions I didn\'t even know existed.' },
  { name: 'Michelle B.', location: 'Austin, TX', text: 'Their education-first approach sets them apart. They don\'t just file your taxes—they help you understand your financial picture. I feel more confident about my finances now.' },
];

export const FAQ_ITEMS = [
  { question: 'How does the 2-day turnaround work?', answer: 'Once we receive all required documents and information from you, our team begins preparation immediately. Most returns are completed within 2 business days. This timeline starts after all documents are received—not from the date of your initial consultation.' },
  { question: 'What does the expedited option include?', answer: 'For an additional $99, your return moves to priority processing. This means faster preparation and review. Expedited processing is available for all services and can be added at any time before preparation begins.' },
  { question: 'What does "starting at" mean for pricing?', answer: 'Our base price of $295 covers standard individual or business returns. More complex situations—such as multiple states, multiple income sources, or business returns with significant activity—may require additional work. We always provide a clear quote before beginning preparation.' },
  { question: 'How do amended returns work?', answer: 'If you need to correct a previously filed return, we review the original return, identify errors or missed items, prepare Form 1040-X (or state equivalent), and submit it to the IRS on your behalf. The flat fee is $500 and includes federal and state amendments if needed.' },
  { question: 'Can you handle multi-state returns?', answer: 'Yes. We prepare returns for all 50 states. Whether you relocated mid-year, work remotely for an out-of-state employer, or have rental properties in different states, we ensure each state return is filed correctly with proper credit allocation.' },
  { question: 'What business returns do you prepare?', answer: 'We prepare Form 1065 (Partnerships), Form 1120S (S-Corporations), and Form 1120 (C-Corporations). Each includes K-1 preparation, state returns, and post-filing support.' },
  { question: 'What documents do I need to upload?', answer: 'Common documents include W-2s, 1099s, prior-year returns, mortgage statements (1098), property tax records, and business financial statements. Our intake form provides a complete checklist based on your filing type.' },
  { question: 'Why does pricing vary?', answer: 'Tax situations differ in complexity. A single W-2 return is straightforward, while a multi-state business return with rental income requires significantly more work. We\'re transparent about pricing and will provide a quote after reviewing your situation.' },
  { question: 'When do I pay?', answer: 'Payment is due after we review your documents and prepare your return. We send an invoice with the final amount before filing. We accept payment via Invoice, Stripe, Square, Zelle, Cash App, and other methods on request.' },
  { question: 'Is my information secure?', answer: 'Yes. We use secure document upload methods and follow industry-standard data protection practices. Your personal and financial information is handled with the highest level of care and confidentiality.' },
  { question: 'What is your refund/cancellation policy?', answer: 'If we have not yet begun preparation of your return, you may cancel without charge. Once preparation has started, fees are non-refundable as the work has been performed. We discuss all fees upfront before beginning any work.' },
  { question: 'How does the process work?', answer: 'You book a consultation, submit documents through our secure intake form, and we prepare your return. We communicate via phone, email, or video call based on your preference. You can also visit our Phoenix, AZ office by appointment.' },
];

export const BUSINESS_STRUCTURES = [
  {
    type: 'LLC (Limited Liability Company)',
    description: 'The most popular structure for small businesses. Provides liability protection with flexible tax options.',
    taxImplications: 'By default, a single-member LLC is taxed as a sole proprietorship, and a multi-member LLC is taxed as a partnership. LLCs can elect S-Corp or C-Corp taxation.',
    bestFor: 'Solo entrepreneurs, small businesses, and real estate investors seeking liability protection.',
    considerations: ['Pass-through taxation by default', 'Flexible management structure', 'Can elect S-Corp status to reduce self-employment tax', 'State filing requirements vary'],
  },
  {
    type: 'S-Corporation',
    description: 'A tax election that allows profits to pass through to shareholders, avoiding double taxation.',
    taxImplications: 'Income passes through to shareholders\' personal returns. Owners who work in the business must take a reasonable salary, which is subject to payroll taxes.',
    bestFor: 'Businesses with consistent profits looking to reduce self-employment tax burden.',
    considerations: ['Must pay reasonable salary to owner-employees', 'Limited to 100 shareholders', 'Only one class of stock allowed', 'Can save on self-employment taxes'],
  },
  {
    type: 'C-Corporation',
    description: 'A separate legal entity that files its own tax return. Subject to corporate tax rates.',
    taxImplications: 'The corporation pays tax on its profits, and shareholders pay tax again on dividends received. This is known as "double taxation."',
    bestFor: 'Businesses planning to raise outside investment, go public, or retain significant earnings.',
    considerations: ['Subject to corporate tax rates', 'Double taxation on dividends', 'Unlimited shareholders allowed', 'Best for raising capital'],
  },
  {
    type: 'Partnership',
    description: 'A business with two or more owners that share profits, losses, and management responsibilities.',
    taxImplications: 'Partnerships file an informational return (Form 1065) but don\'t pay income tax. Profits and losses pass through to partners\' personal returns via K-1s.',
    bestFor: 'Two or more individuals starting a business together without the formality of a corporation.',
    considerations: ['Pass-through taxation', 'Partners report income on personal returns', 'Flexible profit-sharing agreements', 'Each partner receives a K-1'],
  },
];

export const COMMON_MISTAKES = [
  'Choosing an entity type based on trend rather than tax situation',
  'Not understanding self-employment tax implications',
  'Skipping the EIN application or state registrations',
  'Mixing personal and business finances from day one',
  'Not planning for quarterly estimated tax payments',
  'Overlooking state-specific filing requirements',
];

export const COMPANY_VALUES = [
  { title: 'Education First', description: 'We believe informed clients make better financial decisions. Every interaction is an opportunity to teach.', icon: 'GraduationCap' },
  { title: 'Clarity', description: 'No jargon, no surprises. We explain everything in plain language and provide clear pricing upfront.', icon: 'Eye' },
  { title: 'Compliance', description: 'We stay current with tax law changes and ensure every return meets IRS and state requirements.', icon: 'ShieldCheck' },
  { title: 'Long-Term Foundation', description: 'We help clients build a financial structure that supports growth—not just this year, but for years to come.', icon: 'TrendingUp' },
];

export const PAYMENT_METHODS = ['Invoice', 'Stripe', 'Square', 'Zelle', 'Cash App'];

export const CONTACT_INFO = {
  phone: '602-858-1099',
  hours: 'Mon–Fri 9:00 AM – 5:00 PM',
  address: '4539 N. 22nd #8051',
  cityStateZip: 'Phoenix, AZ 85016',
  fullAddress: '4539 N. 22nd #8051, Phoenix, AZ 85016',
  calendlyUrl: 'https://calendly.com/korey_will',
};

export const FILING_TYPES = ['Personal', 'Business', 'Both'];
export const SERVICE_TYPES = ['Tax Preparation', 'Amended Return', 'Business Structure / Formation', 'Multi-State Filing'];
export const CONTACT_METHODS = ['Phone', 'Email', 'Video Call'];
export const US_STATES = ['Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming','District of Columbia'];
